import { Component, computed, OnInit, Signal, WritableSignal } from '@angular/core'
import { NaturalStoneService } from '../../../services/natural-stone.service'
import { SelectValue } from '../../../interfaces/select-value.interface'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { SelectComponent } from '@components/select/select.component'
import { SearchInputComponent } from '@components/search-input/search-input.component'

@Component({
    selector: 'app-natural-stone-color-filters',
    standalone: true,
    imports: [
        SelectComponent,
        TranslateModule,
        SearchInputComponent
    ],
    templateUrl: './natural-stone-color-filters.component.html',
    styleUrl: './natural-stone-color-filters.component.scss'
})
export class NaturalStoneColorFiltersComponent implements OnInit {

    centerOptions: Signal<SelectValue[]>
    selectedCenterValue: Signal<SelectValue | null>

    brandOptions: Signal<SelectValue[]>
    selectedBrandValue: Signal<SelectValue | null>

    thicknessOptions: Signal<SelectValue[]>
    selectedThicknessValue: Signal<SelectValue | null>

    finishOptions: Signal<SelectValue[]>
    selectedFinishValue: Signal<SelectValue | null>

    priceGroupOptions: Signal<SelectValue[]>
    selectedPriceGroupValue: Signal<SelectValue | null>

    searchString: WritableSignal<string>

    orderByOptions: Signal<SelectValue[]>
    selectedOrderByValue: Signal<SelectValue | null>

    constructor(private naturalStoneService: NaturalStoneService, private translate: TranslateService) {
    }

    ngOnInit() {
        this.setupCenters()
        this.setupBrands()
        this.setupThicknesses()
        this.setupFinishes()
        this.setupPriceGroups()
        this.setupSearchString()
        this.setupOrderBy()
    }

    setupCenters() {
        this.centerOptions = computed(() => {
            const centers = this.naturalStoneService.centerOptions()
            return centers.map(center => ({
                id: center.centerId,
                value: center.centerId,
                label: center.centerName
            }))
        })
        this.selectedCenterValue = computed(() => {
            return this.naturalStoneService.selectedCenter() ? {
                id: this.naturalStoneService.selectedCenter(),
                value: this.naturalStoneService.selectedCenter(),
                label: this.centerOptions().find(
                    (center) => center.value === this.naturalStoneService.selectedCenter()
                )?.label || ''
            } : null
        })
    }

    setupBrands() {
        this.brandOptions = computed(() => {
            const brands = this.naturalStoneService.brandOptions()
            return brands.map(brand => ({
                id: brand,
                value: brand,
                label: brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
            }))
        })
        this.selectedBrandValue = computed(() => {
            return this.naturalStoneService.selectedBrand() ? {
                id: this.naturalStoneService.selectedBrand(),
                value: this.naturalStoneService.selectedBrand(),
                label: this.brandOptions().find(
                    (brand) => brand.value === this.naturalStoneService.selectedBrand()
                )?.label || ''
            } : null
        })
    }

    setupThicknesses() {
        this.thicknessOptions = computed(() => {
            const thicknesses = this.naturalStoneService.thicknessOptions()
            return thicknesses.map(thickness => ({
                id: thickness,
                value: thickness,
                label: thickness + ' cm'
            }))
        })
        this.selectedThicknessValue = computed(() => {
            return this.naturalStoneService.selectedThickness() ? {
                id: this.naturalStoneService.selectedThickness(),
                value: this.naturalStoneService.selectedThickness(),
                label: this.naturalStoneService.selectedThickness() + ' cm'
            } : null
        })
    }

    setupFinishes() {
        this.finishOptions = computed(() => {
            const finishes = this.naturalStoneService.finishOptions()
            return finishes.map(finish => ({
                id: finish,
                value: finish,
                label: !this.translate.instant('finishes.' + finish).includes('finishes.') ?
                    this.translate.instant('finishes.' + finish) :
                    finish
            }))
        })
        this.selectedFinishValue = computed(() => {
            return this.naturalStoneService.selectedFinish() ? {
                id: this.naturalStoneService.selectedFinish(),
                value: this.naturalStoneService.selectedFinish(),
                label: this.finishOptions().find(
                    (finish) => finish.value === this.naturalStoneService.selectedFinish()
                )?.label || ''
            } : null
        })
    }

    setupPriceGroups() {
        this.priceGroupOptions = computed(() => {
            const priceGroups = this.naturalStoneService.priceGroupOptions()
            return priceGroups.map(priceGroup => ({
                id: priceGroup,
                value: priceGroup,
                label: this.translate.instant('natural_stone.group') + ' ' + priceGroup
            }))
        })
        this.selectedPriceGroupValue = computed(() => {
            return this.naturalStoneService.selectedPriceGroup() ? {
                id: this.naturalStoneService.selectedPriceGroup(),
                value: this.naturalStoneService.selectedPriceGroup(),
                label: this.translate.instant('natural_stone.group') + ' ' + this.naturalStoneService.selectedPriceGroup()
            } : null
        })
    }

    setupSearchString() {
        this.searchString = this.naturalStoneService.searchString
    }

    setupOrderBy() {
        this.orderByOptions = computed(() => {
            const orderByOptions = this.naturalStoneService.orderByOptions()
            return orderByOptions.map(orderBy => ({
                id: orderBy,
                value: orderBy,
                label: this.translate.instant('natural_stone.order_by.' + orderBy)
            }))
        })

        this.selectedOrderByValue = computed(() => {
            return this.naturalStoneService.orderBy() ? {
                id: this.naturalStoneService.orderBy(),
                value: this.naturalStoneService.orderBy(),
                label: this.orderByOptions().find(
                    (orderBy) => orderBy.value === this.naturalStoneService.orderBy()
                )?.label || ''
            } : null
        })

    }

    changeCenter(newCenter: SelectValue | null) {
        if (!newCenter) return
        this.naturalStoneService.changeCenter(newCenter.value)
    }

    changeBrand(newBrand: SelectValue | null){
        if(newBrand === this.selectedBrandValue()) return
        this.naturalStoneService.changeBrand(newBrand ? newBrand.value : '')
    }

    changeFinish(newFinish: SelectValue | null){
        if(newFinish === this.selectedFinishValue()) return
        this.naturalStoneService.changeFinish(newFinish ? newFinish.value : '')
    }

    changeThickness(newThickness: SelectValue | null){
        if(newThickness === this.selectedThicknessValue()) return
        this.naturalStoneService.changeThickness(newThickness ? newThickness.value : '')
    }

    changePriceGroup(newPriceGroup: SelectValue | null){
        if(newPriceGroup === this.selectedPriceGroupValue()) return
        this.naturalStoneService.changePriceGroup(newPriceGroup ? newPriceGroup.value : '')
    }

    changeSearchString(newSearchString?: string) {
        const searchString = newSearchString || ''
        this.naturalStoneService.changeSearchString(searchString)
    }

    changeOrderBy(newOrderBy: SelectValue | null){
        if(!newOrderBy || newOrderBy === this.selectedOrderByValue()) return
        this.naturalStoneService.changeOrderBy(newOrderBy ? newOrderBy.value : '')
    }

}
